import { useState, useMemo, useCallback, useEffect } from 'react'
import { Col, Checkbox, Row, Popover, Table } from 'antd'
import { CloseOutlined } from '@ant-design/icons'
import MenuIcon from '../Svg/MenuIcon'
import { filterDistinctByProperty, renderTranslation, stopPropagation } from '../../Helpers/GenericHelpers'
import { TranslationKeys } from '../../Constants/TranslationKeys'
import { CheckboxLangClass, defaultSectorId, defaultSupergroupId } from '../../Constants/Configuration'
import { setCapitalLetter } from '../../Helpers/StringHelpers'
import {
  rangeRowFilters,
  rangefilterByRange,
  rangefilterBySector,
  rangefilterBySupergroup,
} from '../../Constants/TableConfiguration'
import {
  hasRanges,
  hasSectors,
  hasSupergroups,
  retrieveCommissions,
  retrieveRangeThresholds,
  retrieveCommission,
  retrieveSectorSupergroupValue,
  retrieveSectorValue,
  retrieveSupergroupValue,
  isTableCommissionsSectorFilterSet,
  isTableCommissionsSupergroupFilterSet,
  isTableCommissionsRangeFilterSet,
  hasSupergroupsSectors,
} from '../../Helpers/ChannelSettingsHelper'
import { removeThreshold, setCommissionsToSave } from '../../Slices/Settings/ChannelSlice'
import ChannelInput from './ChannelInput'
import { useAppDispatch, useAppSelector } from '../../hooks'

function ChannelCommissionsRangeRow(props) {
  const dispatch = useAppDispatch()
  const language = useAppSelector(state => state.present.language.value)
  const channelList = useAppSelector(state => state.present.channel.channelList)
  const sectors = useAppSelector(state => state.present.channel.sectorsFilter)
  const supergroups = useAppSelector(state => state.present.channel.supergroupsFilter)

  const [popoverVisible, setPopoverVisible] = useState(false)
  const [tableCommissionFilter, setTableCommissionFilter] = useState([])

  const currentComissions = useMemo(() => {
    return retrieveCommissions(channelList, props.id)
  }, [channelList, props.id])

  const hasCommissionsRanges = hasRanges(currentComissions)

  const hasCommissionsSectors = hasSectors(currentComissions)

  const hasCommissionsSupergroups = hasSupergroups(currentComissions)

  const hasCommissionSupergroupsSectors = hasSupergroupsSectors(currentComissions)

  const currentRangesThreshold = useMemo(() => {
    return retrieveRangeThresholds(currentComissions)
  }, [currentComissions])

  const commission = useMemo(() => {
    return retrieveCommission(currentComissions)
  }, [currentComissions])

  const thresholds = currentRangesThreshold.filter((value, index, self) =>
    filterDistinctByProperty(value, index, self, 'threshold'),
  )

  useEffect(() => {
    const activeFilters = [...tableCommissionFilter]
    if (hasCommissionsRanges && !isTableCommissionsRangeFilterSet(activeFilters)) {
      activeFilters.push(rangefilterByRange)
    }

    if (
      (hasCommissionsSectors || hasCommissionSupergroupsSectors) &&
      !isTableCommissionsSectorFilterSet(activeFilters)
    ) {
      activeFilters.push(rangefilterBySector)
    }

    if (hasCommissionsSupergroups && !isTableCommissionsSupergroupFilterSet(activeFilters)) {
      activeFilters.push(rangefilterBySupergroup)
    }

    setTableCommissionFilter(activeFilters)
  }, [])

  const onChangeCheckbox = useCallback(checkedValue => {
    setTableCommissionFilter(checkedValue)
  }, [])

  const addRange = useCallback(() => {
    const mappedThresholds = thresholds.map(x => x.threshold)
    let maxThreshold = Math.max.apply(Math, mappedThresholds)
    if (maxThreshold === -Infinity || maxThreshold === Infinity) {
      maxThreshold = 0
    }

    const rangeData = {
      value: 0,
      threshold: maxThreshold + 1,
      sectorId: defaultSectorId,
      supergroupId: defaultSupergroupId,
    }

    if (tableCommissionFilter.indexOf(rangefilterByRange) === -1) {
      setTableCommissionFilter(oldArray => {
        return [...oldArray, rangefilterByRange]
      })
    }

    dispatch(
      setCommissionsToSave({
        name: props.id,
        commission: rangeData,
        date: new Date().toISOString(),
      }),
    )
  }, [dispatch, props.id, tableCommissionFilter, thresholds])

  const tableDataFilterCheckbox = useMemo(() => {
    return (
      <Checkbox.Group className='' onChange={onChangeCheckbox} value={tableCommissionFilter}>
        <Row onClick={stopPropagation}>
          {rangeRowFilters.map(value => {
            return (
              <Col key={value} span={24} className='exclude'>
                <Checkbox className={'general-checkbox exclude'} value={value}>
                  {renderTranslation(
                    language,
                    TranslationKeys[CheckboxLangClass + setCapitalLetter(value) + 'ChannelRange'],
                  )}
                </Checkbox>
              </Col>
            )
          })}
        </Row>
      </Checkbox.Group>
    )
  }, [tableCommissionFilter, language, onChangeCheckbox])

  const generateSupergroupColumns = useCallback(
    (column, threshold, sectorId) => {
      const dataIndexName = 'supergroup' + (threshold ? threshold : 'Default') + (sectorId ? sectorId : '')
      column['dataIndex'] = dataIndexName
      column['key'] = dataIndexName
      column['render'] = (text, record) => {
        return (
          <ChannelInput
            channelName={props.id}
            value={text}
            threshold={threshold}
            sectorId={sectorId}
            supergroupId={record.rowId}
            max={100}
          />
        )
      }
    },
    [props.id],
  )

  const generateSectorColumnsChildren = useCallback(
    (rangeColumn, threshold) => {
      //if supergroup filter is active sector column change in sectorSupergroup column
      const isSupergroupFilterEnable = isTableCommissionsSupergroupFilterSet(tableCommissionFilter)
      const sectorColumnKeyName = isSupergroupFilterEnable ? 'sectorSupergroup' : 'sector'
      const rangeColumnChildrens = sectors.map(sector => {
        return {
          title: <div>{sector.name}</div>,
          dataIndex: sectorColumnKeyName + (threshold ? threshold : 'Default') + sector.id,
          key: sectorColumnKeyName + (threshold ? threshold : 'Default') + sector.id,
          align: 'center',
          width: 90,
          render: (text, record) => {
            return (
              <ChannelInput
                channelName={props.id}
                value={text}
                threshold={threshold}
                sectorId={sector.id}
                supergroupId={isSupergroupFilterEnable ? record.rowId : null}
                max={100}
              />
            )
          },
        }
      })

      rangeColumn['children'] = rangeColumnChildrens
    },
    [tableCommissionFilter, props.id, sectors],
  )

  const rangeColumns = useMemo(() => {
    const columns = []

    columns.push({
      title: (
        <Popover
          id='channelRangeCheckbox'
          className='exclude-click'
          content={tableDataFilterCheckbox}
          placement='bottomLeft'
          autoAdjustOverflow={true}
          trigger='click'
          open={popoverVisible}
          onOpenChange={setPopoverVisible}
        >
          <MenuIcon onClick={stopPropagation} className={'generic-icon menu-icon-variant'} />
        </Popover>
      ),
      dataIndex: 'supergroupNameColumn',
      key: 'supergroupNameColumn',
      width: 200,
      fixed: 'left',
      scroll: { x: 'max-content' },
      render: (text, record) => {
        return <div>{text}</div>
      },
    })

    const generalRangeColumn = {
      title: (
        <div className='d-flex-c pa-1'>
          <div>
            {thresholds && thresholds.length > 0 && tableCommissionFilter.indexOf(rangefilterByRange) !== -1
              ? renderTranslation(language, TranslationKeys.channelRangeOver)
              : renderTranslation(language, TranslationKeys.channelRangeCommission)}
          </div>
          <ChannelInput
            channelName={props.id}
            value={commission && commission.value ? commission.value : 0}
            max={100}
            isPercentageHeader={true}
          />
        </div>
      ),
      align: 'left',
    }

    if (isTableCommissionsRangeFilterSet(tableCommissionFilter)) {
      thresholds.forEach(range => {
        const rangeColumn = {
          title: (
            <div className='d-flex-c pa-1'>
              <div className='mr-1'>{renderTranslation(language, TranslationKeys.channelRangeFromTo)}</div>
              <div className='mr-1'>
                <ChannelInput
                  channelName={props.id}
                  isRange={true}
                  isThreshold={true}
                  value={range.value}
                  threshold={range.threshold}
                  max={Number.MAX_SAFE_INTEGER}
                />
              </div>
              <div>
                <ChannelInput
                  channelName={props.id}
                  isRange={true}
                  value={range.value}
                  threshold={range.threshold}
                  max={100}
                  isPercentageHeader={true}
                />
              </div>
              <div className='close'>
                <CloseOutlined
                  className='close-icon'
                  onClick={() => {
                    dispatch(
                      removeThreshold({
                        name: props.id,
                        threshold: range.threshold,
                        date: new Date().toISOString(),
                      }),
                    )
                  }}
                />
              </div>
            </div>
          ),
          align: 'left',
        }

        if (isTableCommissionsSectorFilterSet(tableCommissionFilter)) {
          generateSectorColumnsChildren(rangeColumn, range.threshold)
        } else {
          if (isTableCommissionsSupergroupFilterSet(tableCommissionFilter)) {
            generateSupergroupColumns(rangeColumn, range.threshold, null)
          }
        }

        columns.push(rangeColumn)
      })
    }

    if (isTableCommissionsSectorFilterSet(tableCommissionFilter)) {
      generateSectorColumnsChildren(generalRangeColumn, null)
    } else {
      if (isTableCommissionsSupergroupFilterSet(tableCommissionFilter)) {
        generateSupergroupColumns(generalRangeColumn, null, null)
      }
    }

    columns.push(generalRangeColumn)

    columns.push({
      title: <div onClick={addRange}>{renderTranslation(language, TranslationKeys.channelRangeAdd)}</div>,
      dataIndex: 'addRange',
      key: 'addRange',
      width: 80,
      fixed: 'right',
      align: 'right',
    })

    return columns
  }, [
    tableDataFilterCheckbox,
    popoverVisible,
    thresholds,
    tableCommissionFilter,
    language,
    props.id,
    commission,
    addRange,
    dispatch,
    generateSectorColumnsChildren,
    generateSupergroupColumns,
  ])

  const dataSource = useMemo(() => {
    const tableData = []

    if (isTableCommissionsSupergroupFilterSet(tableCommissionFilter)) {
      supergroups.forEach(supergroup => {
        const supergroupRowData = {}
        supergroupRowData['rowId'] = supergroup.id
        supergroupRowData['supergroupNameColumn'] = supergroup.name

        if (isTableCommissionsRangeFilterSet(tableCommissionFilter)) {
          if (isTableCommissionsSectorFilterSet(tableCommissionFilter)) {
            sectors.forEach(sector => {
              currentRangesThreshold.forEach(range => {
                const supergroupValue = retrieveSectorSupergroupValue(
                  currentComissions,
                  supergroup.id,
                  sector.id,
                  range.threshold,
                )
                if (supergroupValue) {
                  const supergroupDataIndexName = 'sectorSupergroup' + range.threshold + (sector.id ? sector.id : '')
                  supergroupRowData[supergroupDataIndexName] = supergroupValue
                }
              })

              const supergroupValue = retrieveSectorSupergroupValue(currentComissions, supergroup.id, sector.id, null)
              if (supergroupValue) {
                supergroupRowData['sectorSupergroupDefault' + (sector.id ? sector.id : '')] = supergroupValue
              }
            })
          } else {
            currentRangesThreshold.forEach(range => {
              const supergroupValue = retrieveSupergroupValue(currentComissions, supergroup.id, range.threshold)
              if (supergroupValue) {
                supergroupRowData['supergroup' + range.threshold] = supergroupValue
              }
            })

            const supergroupValue = retrieveSupergroupValue(currentComissions, supergroup.id, null)
            if (supergroupValue) {
              supergroupRowData['supergroupDefault'] = supergroupValue
            }
          }
        } else {
          if (isTableCommissionsSectorFilterSet(tableCommissionFilter)) {
            sectors.forEach(sector => {
              const supergroupValue = retrieveSectorSupergroupValue(currentComissions, supergroup.id, sector.id, null)
              if (supergroupValue) {
                supergroupRowData['sectorSupergroupDefault' + (sector.id ? sector.id : '')] = supergroupValue
              }
            })
          } else {
            const supergroupValue = retrieveSupergroupValue(currentComissions, supergroup.id, null)
            if (supergroupValue) {
              supergroupRowData['supergroupDefault'] = supergroupValue
            }
          }
        }

        tableData.push(supergroupRowData)
      })
    } else {
      const tableRowData = {}
      tableRowData['rowId'] = 'sectorData'
      if (isTableCommissionsRangeFilterSet(tableCommissionFilter)) {
        if (isTableCommissionsSectorFilterSet(tableCommissionFilter)) {
          sectors.forEach(sector => {
            currentRangesThreshold.forEach(range => {
              const sectorValue = retrieveSectorValue(currentComissions, sector.id, range.threshold)
              if (sectorValue) {
                tableRowData['sector' + range.threshold + sector.id] = sectorValue
              }
            })

            const sectorValue = retrieveSectorValue(currentComissions, sector.id, null)
            if (sectorValue) {
              tableRowData['sectorDefault' + (sector.id ? sector.id : '')] = sectorValue
            }
          })
        }
      } else {
        if (isTableCommissionsSectorFilterSet(tableCommissionFilter)) {
          sectors.forEach(sector => {
            const sectorValue = retrieveSectorValue(currentComissions, sector.id, null)
            if (sectorValue) {
              tableRowData['sectorDefault' + (sector.id ? sector.id : '')] = sectorValue
            }
          })
        }
      }

      tableData.push(tableRowData)
    }

    return tableData
  }, [tableCommissionFilter, supergroups, sectors, currentRangesThreshold, currentComissions])

  return (
    <Row className='background-thirt pa-2'>
      <Table
        id='commission-table'
        columns={rangeColumns}
        rowKey={record => {
          return 'key-' + record.rowId
        }}
        dataSource={dataSource}
        pagination={false}
        tableLayout='fixed'
        scroll={{ x: 'auto' }}
        className={
          'commission-table' +
          (isTableCommissionsSectorFilterSet(tableCommissionFilter) &&
          isTableCommissionsSupergroupFilterSet(tableCommissionFilter)
            ? ' hide-table-body'
            : '')
        }
      />
    </Row>
  )
}

export default ChannelCommissionsRangeRow
